import { z } from 'zod';

/**
 * MYTAdmin-style financial / sales reporting schemas (redevelopment plan
 * docs/mytadmin-financial-reporting-redevelopment-plan.md). One file per build
 * phase of reports; all money is BigInt paise as decimal strings, dates ISO.
 */

// ============================================================================
// Report #1 - Booking Detail (Sales register)
// One row per invoice: sale / supplier cost / margin / GST / received / due /
// clear-days, scoped by branch / corporate / agent / reporting-manager.
// ============================================================================

export const SalesRegisterCorporateSchema = z.enum(['ALL', 'CORPORATE', 'RETAIL']);
export type SalesRegisterCorporate = z.infer<typeof SalesRegisterCorporateSchema>;

export const SalesRegisterQuerySchema = z.object({
  /** Invoice-date lower bound (ISO). */
  from: z.string().optional(),
  /** Invoice-date upper bound (ISO). */
  to: z.string().optional(),
  branchId: z.string().uuid().optional(),
  agentId: z.string().uuid().optional(),
  /** Roll up to all agents reporting (directly) to this manager. */
  managerId: z.string().uuid().optional(),
  corporate: SalesRegisterCorporateSchema.default('ALL'),
  /** Free text over invoice number / customer / agent. */
  search: z.string().optional(),
});
export type SalesRegisterQuery = z.infer<typeof SalesRegisterQuerySchema>;

export const SalesRegisterRowSchema = z.object({
  invoiceId: z.string(),
  invoiceNumber: z.string(),
  proformaNumber: z.string().nullable(),
  taxInvoiceNumber: z.string().nullable(),
  /** Invoice created date (ISO). */
  invoiceDate: z.string().nullable(),
  customerName: z.string().nullable(),
  agentName: z.string().nullable(),
  branchName: z.string().nullable(),
  productType: z.string().nullable(),
  isCorporate: z.boolean(),
  status: z.string(),
  grossSalePaise: z.string(),
  supplierCostPaise: z.string(),
  marginPaise: z.string(),
  gstPaise: z.string(),
  grandTotalPaise: z.string(),
  receivedPaise: z.string(),
  duePaise: z.string(),
  /** Days from invoice date to the receipt that cleared it; null if still due. */
  clearDays: z.number().int().nullable(),
});
export type SalesRegisterRow = z.infer<typeof SalesRegisterRowSchema>;

export const SalesRegisterTotalsSchema = z.object({
  count: z.number().int(),
  grossSalePaise: z.string(),
  supplierCostPaise: z.string(),
  marginPaise: z.string(),
  gstPaise: z.string(),
  grandTotalPaise: z.string(),
  receivedPaise: z.string(),
  duePaise: z.string(),
});
export type SalesRegisterTotals = z.infer<typeof SalesRegisterTotalsSchema>;

export const SalesRegisterResponseSchema = z.object({
  rows: z.array(SalesRegisterRowSchema),
  totals: SalesRegisterTotalsSchema,
});
export type SalesRegisterResponse = z.infer<typeof SalesRegisterResponseSchema>;

// ============================================================================
// Report #10 - Client Outstanding
// Invoices where billed > received (money still owed by the customer), with the
// days outstanding since the invoice date.
// ============================================================================

export const ClientOutstandingQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  agentId: z.string().uuid().optional(),
  /** Free text over invoice number / customer / email / agent. */
  search: z.string().optional(),
});
export type ClientOutstandingQuery = z.infer<typeof ClientOutstandingQuerySchema>;

export const ClientOutstandingRowSchema = z.object({
  invoiceId: z.string(),
  invoiceNumber: z.string(),
  proformaNumber: z.string().nullable(),
  invoiceDate: z.string().nullable(),
  customerName: z.string().nullable(),
  customerEmail: z.string().nullable(),
  agentName: z.string().nullable(),
  grandTotalPaise: z.string(),
  receivedPaise: z.string(),
  outstandingPaise: z.string(),
  /** Whole days since the invoice date (as of now). */
  daysOutstanding: z.number().int(),
});
export type ClientOutstandingRow = z.infer<typeof ClientOutstandingRowSchema>;

export const ClientOutstandingTotalsSchema = z.object({
  count: z.number().int(),
  grandTotalPaise: z.string(),
  receivedPaise: z.string(),
  outstandingPaise: z.string(),
});
export type ClientOutstandingTotals = z.infer<typeof ClientOutstandingTotalsSchema>;

export const ClientOutstandingResponseSchema = z.object({
  rows: z.array(ClientOutstandingRowSchema),
  totals: ClientOutstandingTotalsSchema,
});
export type ClientOutstandingResponse = z.infer<typeof ClientOutstandingResponseSchema>;

// ============================================================================
// Report #11 - Supplier Paid But Client Outstanding
// Cash-flow risk: invoices where we have paid the supplier but the customer has
// paid nothing yet.
// ============================================================================

export const SupplierPaidOutstandingQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  agentId: z.string().uuid().optional(),
  search: z.string().optional(),
});
export type SupplierPaidOutstandingQuery = z.infer<typeof SupplierPaidOutstandingQuerySchema>;

export const SupplierPaidOutstandingRowSchema = z.object({
  invoiceId: z.string(),
  invoiceNumber: z.string(),
  proformaNumber: z.string().nullable(),
  invoiceDate: z.string().nullable(),
  customerName: z.string().nullable(),
  agentName: z.string().nullable(),
  grandTotalPaise: z.string(),
  /** Total already paid out to suppliers on this invoice (cash + TDS). */
  supplierPaidPaise: z.string(),
  /** Still owed by the customer (grand total - credits; client has paid 0). */
  outstandingPaise: z.string(),
  daysOutstanding: z.number().int(),
});
export type SupplierPaidOutstandingRow = z.infer<typeof SupplierPaidOutstandingRowSchema>;

export const SupplierPaidOutstandingTotalsSchema = z.object({
  count: z.number().int(),
  grandTotalPaise: z.string(),
  supplierPaidPaise: z.string(),
  outstandingPaise: z.string(),
});
export type SupplierPaidOutstandingTotals = z.infer<typeof SupplierPaidOutstandingTotalsSchema>;

export const SupplierPaidOutstandingResponseSchema = z.object({
  rows: z.array(SupplierPaidOutstandingRowSchema),
  totals: SupplierPaidOutstandingTotalsSchema,
});
export type SupplierPaidOutstandingResponse = z.infer<typeof SupplierPaidOutstandingResponseSchema>;

// ============================================================================
// Report #13 - Balance Due Report (1:1 with legacy balanceduereport.aspx +
// GetBalanceDueDateBySearchCriteriaV4). Unpivots the 4 deposit installment
// slots into one row each (where deposit > 0), grouped by balance-due date
// (DESC) with per-date sub-totals + a grand total. Default window when no
// filter is applied: today .. today + 10 days.
// ============================================================================

export const BalanceDuePaymentStatusSchema = z.enum(['ALL', 'PAID', 'UNPAID']);
export type BalanceDuePaymentStatus = z.infer<typeof BalanceDuePaymentStatusSchema>;

export const BalanceDueReportQuerySchema = z.object({
  /** Proforma / booking number (exact), legacy @bookingId. */
  bookingId: z.string().optional(),
  /** Balance-due-date lower / upper bound (ISO date). */
  from: z.string().optional(),
  to: z.string().optional(),
  /** Exact installment amount in rupees, legacy @amount. */
  amount: z.coerce.number().optional(),
  paymentStatus: BalanceDuePaymentStatusSchema.default('ALL'),
  /** Agent name (travel_agent_name), legacy @assignedTo. */
  agent: z.string().optional(),
});
export type BalanceDueReportQuery = z.infer<typeof BalanceDueReportQuerySchema>;

export const BalanceDueReportRowSchema = z.object({
  invoiceId: z.string(),
  proformaNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  agentName: z.string().nullable(),
  /** This installment's amount. */
  amountPaise: z.string(),
  /** This installment's due date (ISO), or null. */
  dueDate: z.string().nullable(),
  paid: z.boolean(),
  /** Which of the 4 deposit slots (1-4) this row came from. */
  slot: z.number().int(),
});
export type BalanceDueReportRow = z.infer<typeof BalanceDueReportRowSchema>;

export const BalanceDueReportResponseSchema = z.object({
  /** Ordered by due date DESC (the page groups by date for sub-totals). */
  rows: z.array(BalanceDueReportRowSchema),
  totalPaise: z.string(),
});
export type BalanceDueReportResponse = z.infer<typeof BalanceDueReportResponseSchema>;

// ============================================================================
// Report #19 - Bank Transaction Statement (1:1 with banktransactionstatement
// .aspx + GetBankTransactionStatementsV3). A Dr/Cr passbook: customer receipts
// are Dr (money in), supplier payments are Cr (money out), one row per paid
// transaction, ordered by date DESC. (The legacy service-tax Cr leg has no
// equivalent ledger in our data and is omitted.) Default window when no filter:
// today - 10 days .. today.
// ============================================================================

export const BankStatementQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  /** Company bank account UUID (legacy @bankId). */
  bankAccountId: z.string().uuid().optional(),
  /** Agent name (travel_agent_name), legacy @agent. */
  agent: z.string().optional(),
  /** Customer email contains, legacy @emailId. */
  email: z.string().optional(),
  /** GST/billing customer name contains, legacy @GSTCustomerName. */
  gstCustomerName: z.string().optional(),
});
export type BankStatementQuery = z.infer<typeof BankStatementQuerySchema>;

export const BankStatementRowSchema = z.object({
  invoiceId: z.string(),
  proformaNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  email: z.string().nullable(),
  gstCustomerName: z.string().nullable(),
  /** Transaction date (ISO). */
  date: z.string().nullable(),
  /** Money in (customer receipt); "0" on a Cr row. */
  amountDrPaise: z.string(),
  /** Money out (supplier payment); "0" on a Dr row. */
  amountCrPaise: z.string(),
});
export type BankStatementRow = z.infer<typeof BankStatementRowSchema>;

export const BankStatementResponseSchema = z.object({
  rows: z.array(BankStatementRowSchema),
  totalDrPaise: z.string(),
  totalCrPaise: z.string(),
});
export type BankStatementResponse = z.infer<typeof BankStatementResponseSchema>;

// ============================================================================
// Report #33 - Operation Pending (1:1 with operationpendingreport.aspx +
// GetOperationPendingReport). Ops follow-up: bookings whose check-in is in the
// future and which are PARTIALLY paid (received > 0 but still owing). Ordered by
// check-in date DESC.
// ============================================================================

export const OperationPendingQuerySchema = z.object({
  /** Check-in (departure) date range. */
  checkInFrom: z.string().optional(),
  checkInTo: z.string().optional(),
  /** Pax / customer name contains. */
  paxName: z.string().optional(),
  /** Proforma / booking number (exact), legacy @bookingId. */
  bookingId: z.string().optional(),
  /** Agent name (travel_agent_name). */
  agent: z.string().optional(),
  /** Check-in month (1-12). */
  month: z.coerce.number().int().min(1).max(12).optional(),
  /** Check-in year. */
  year: z.coerce.number().int().optional(),
});
export type OperationPendingQuery = z.infer<typeof OperationPendingQuerySchema>;

export const OperationPendingRowSchema = z.object({
  invoiceId: z.string(),
  proformaNumber: z.string().nullable(),
  paxName: z.string().nullable(),
  destination: z.string().nullable(),
  outstandingPaise: z.string(),
  checkInDate: z.string().nullable(),
  checkOutDate: z.string().nullable(),
  agentName: z.string().nullable(),
});
export type OperationPendingRow = z.infer<typeof OperationPendingRowSchema>;

export const OperationPendingResponseSchema = z.object({
  /** Ordered by check-in date DESC. */
  rows: z.array(OperationPendingRowSchema),
  totalOutstandingPaise: z.string(),
});
export type OperationPendingResponse = z.infer<typeof OperationPendingResponseSchema>;

// ============================================================================
// Report (Deposit Booking) - 1:1 with depositbookingreport.aspx +
// GetBookingDetailsByUserIdV1. Bookings created in the window that still carry
// an outstanding balance (grand total > received), newest first, with the full
// booking detail. Default window when no filter: today - 5 days .. now.
// ============================================================================

export const DepositBookingQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  /** Proforma / booking number (exact). */
  bookingId: z.string().optional(),
  customerName: z.string().optional(),
  email: z.string().optional(),
  /** Exact grand total in rupees (legacy @totalAmount). */
  amount: z.coerce.number().optional(),
  /** Agent name (travel_agent_name). */
  agent: z.string().optional(),
});
export type DepositBookingQuery = z.infer<typeof DepositBookingQuerySchema>;

export const DepositBookingRowSchema = z.object({
  invoiceId: z.string(),
  proformaNumber: z.string().nullable(),
  createdDate: z.string().nullable(),
  customerName: z.string().nullable(),
  email: z.string().nullable(),
  mobile: z.string().nullable(),
  grossSalePaise: z.string(),
  supplierCostPaise: z.string(),
  profitPaise: z.string(),
  receivedPaise: z.string(),
  balancePaise: z.string(),
  gstPaise: z.string(),
  destination: z.string().nullable(),
  checkInDate: z.string().nullable(),
  checkOutDate: z.string().nullable(),
  productType: z.string().nullable(),
  agentName: z.string().nullable(),
  branchName: z.string().nullable(),
  querySource: z.string().nullable(),
});
export type DepositBookingRow = z.infer<typeof DepositBookingRowSchema>;

export const DepositBookingTotalsSchema = z.object({
  count: z.number().int(),
  grossSalePaise: z.string(),
  supplierCostPaise: z.string(),
  profitPaise: z.string(),
  receivedPaise: z.string(),
  balancePaise: z.string(),
  gstPaise: z.string(),
});
export type DepositBookingTotals = z.infer<typeof DepositBookingTotalsSchema>;

export const DepositBookingResponseSchema = z.object({
  rows: z.array(DepositBookingRowSchema),
  totals: DepositBookingTotalsSchema,
});
export type DepositBookingResponse = z.infer<typeof DepositBookingResponseSchema>;
